// ============================================================
// frontend/src/components/Brand.tsx
// ============================================================
// The Consilium mark + lockup, as inline SVG so we can animate
// the ring drawing on and tint via CSS currentColor when needed.
//
// The mark: an open ring (a "C") cradling a single gold dot —
// the brand identity from consilium-brand/.
// ============================================================

import type { CSSProperties } from 'react';

interface MarkProps {
  size?: number;
  /** Animate the ring drawing in + the dot fading in on mount. */
  animate?: boolean;
  /** Stagger the draw-on (ms). Useful in lockups so the wordmark sequence works. */
  delay?: number;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
}

/**
 * The mark only — gold ring + dot on a transparent background.
 * Sized via the `size` prop (renders a square).
 */
export function ConsiliumMark({
  size = 40,
  animate = false,
  delay = 0,
  className,
  style,
  ariaLabel = 'Consilium',
}: MarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={ariaLabel}
      className={`consilium-mark${animate ? ' is-animated' : ''}${className ? ` ${className}` : ''}`}
      style={{ ...style, ['--mark-delay' as string]: `${delay}ms` }}
    >
      <path
        className="consilium-mark-ring"
        d="M78.19 39.74 A30 30 0 1 0 60.26 78.19"
        fill="none"
        stroke="var(--accent)"
        strokeWidth="8.5"
        strokeLinecap="round"
      />
      <circle
        className="consilium-mark-dot"
        cx="71.75"
        cy="58.25"
        r="5.6"
        fill="var(--accent)"
      />
    </svg>
  );
}

interface LockupProps {
  /** Vertical height of the lockup in px (mark scales with it, wordmark sits beside). */
  height?: number;
  animate?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Mark + "Consilium" wordmark, side by side. The wordmark is LIVE TEXT
 * (Inter Display) so it crispens at any size and inherits the active theme.
 */
export function ConsiliumLockup({ height = 32, animate = false, className, style }: LockupProps) {
  return (
    <div
      className={`consilium-lockup${animate ? ' is-animated' : ''}${className ? ` ${className}` : ''}`}
      style={{ ...style, ['--lockup-h' as string]: `${height}px` }}
      aria-label="Consilium"
      role="img"
    >
      <ConsiliumMark size={height} animate={animate} ariaLabel="" />
      <span className="consilium-wordmark" aria-hidden="true">Consilium</span>
    </div>
  );
}
